import { useState, useCallback, useEffect } from 'react';
import api from '../../services/api';
import defaultManualContent from './manualContent';

const STORAGE_KEY = 'sk_manual_content';

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const loadContent = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.warn('Failed to load manual from localStorage:', e);
  }
  return deepClone(defaultManualContent);
};

// ── One-time migration: drain any leftover client-side images → server ────

/**
 * Reads all images from IndexedDB (from the previous migration), returns a map,
 * then clears the store so it's never read again.
 */
const drainIndexedDB = () => new Promise((resolve) => {
  try {
    const req = indexedDB.open('sk_manual_db', 1);
    req.onupgradeneeded = (e) => { e.target.transaction.abort(); }; // DB didn't exist
    req.onerror = () => resolve({});
    req.onsuccess = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('images')) { db.close(); resolve({}); return; }
      const getReq = db.transaction('images', 'readonly').objectStore('images').getAll();
      getReq.onsuccess = () => {
        const map = {};
        getReq.result.forEach(({ id, src, caption }) => { map[id] = { src, caption }; });
        const clearTx = db.transaction('images', 'readwrite');
        clearTx.objectStore('images').clear();
        clearTx.oncomplete = () => { db.close(); resolve(map); };
        clearTx.onerror   = () => { db.close(); resolve(map); };
      };
      getReq.onerror = () => { db.close(); resolve({}); };
    };
  } catch (e) { resolve({}); }
});

// ── Server API helpers ─────────────────────────────────────────────────────
// api.get() returns the full axios response; .data is the JSON body.

const apiGetImages = () =>
  api.get('/manual/images').then(r => r.data?.data || {}).catch(() => ({}));

const apiPutImage = (sectionId, src, caption) =>
  api.put(`/manual/images/${encodeURIComponent(sectionId)}`, { src, caption })
     .catch(e => console.warn('Image save failed:', e));

const apiDeleteImage = (sectionId) =>
  api.delete(`/manual/images/${encodeURIComponent(sectionId)}`)
     .catch(e => console.warn('Image delete failed:', e));

const apiClearImages = () =>
  api.delete('/manual/images')
     .catch(e => console.warn('Image clear failed:', e));

// ── Hook ──────────────────────────────────────────────────────────────────

/**
 * useManualStore — central state hook for the editable manual.
 *
 * Text content: localStorage (small, sync).
 * Image overrides: MongoDB via REST API (cross-device, unlimited).
 */
const useManualStore = () => {
  const [content, setContent] = useState(loadContent);
  const [imageOverrides, setImageOverrides] = useState({});

  useEffect(() => {
    (async () => {
      try {
        // 1. Load images from server (source of truth)
        const serverImages = await apiGetImages();

        // 2. Drain any leftover IndexedDB images (previous migration)
        const idbImages = await drainIndexedDB();

        // 3. Drain leftover localStorage images (oldest migration layer)
        let lsImages = {};
        try {
          const raw = localStorage.getItem('sk_manual_images');
          if (raw) { lsImages = JSON.parse(raw); localStorage.removeItem('sk_manual_images'); }
        } catch (e) { /* ignore */ }

        // 4. Upload any locally-saved images the server doesn't have yet
        const local = { ...lsImages, ...idbImages };
        const merged = { ...serverImages };
        const uploads = [];
        for (const [id, { src, caption }] of Object.entries(local)) {
          if (!merged[id]) {
            merged[id] = { src, caption };
            uploads.push(apiPutImage(id, src, caption));
          }
        }
        if (uploads.length) await Promise.allSettled(uploads);

        setImageOverrides(merged);
      } catch (e) {
        console.warn('Failed to load manual images:', e);
      }
    })();
  }, []);

  const persist = useCallback((newContent) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newContent));
    } catch (e) {
      console.warn('Failed to persist manual content:', e);
    }
  }, []);

  const updateMeta = useCallback((field, value) => {
    setContent((prev) => {
      const next = { ...prev, meta: { ...prev.meta, [field]: value } };
      persist(next);
      return next;
    });
  }, [persist]);

  const updateSection = useCallback((chapterId, sectionId, field, value) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      sec[field] = value;
      persist(next);
      return next;
    });
  }, [persist]);

  const updateStepItem = useCallback((chapterId, sectionId, index, value) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      const arr = sec.steps || sec.items || [];
      arr[index] = value;
      persist(next);
      return next;
    });
  }, [persist]);

  const addStep = useCallback((chapterId, sectionId) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      if (sec.steps) sec.steps.push('');
      else if (sec.items) sec.items.push('');
      persist(next);
      return next;
    });
  }, [persist]);

  const removeStep = useCallback((chapterId, sectionId, index) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      if (sec.steps) sec.steps.splice(index, 1);
      else if (sec.items) sec.items.splice(index, 1);
      persist(next);
      return next;
    });
  }, [persist]);

  const moveStep = useCallback((chapterId, sectionId, fromIndex, toIndex) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      const arr = sec.steps || sec.items;
      if (!arr) return prev;
      const [item] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, item);
      persist(next);
      return next;
    });
  }, [persist]);

  // Images → server (MongoDB), fire-and-forget async
  const updateImage = useCallback((sectionId, src, caption) => {
    setImageOverrides((prev) => ({ ...prev, [sectionId]: { src, caption } }));
    apiPutImage(sectionId, src, caption);
  }, []);

  const removeImageOverride = useCallback((sectionId) => {
    setImageOverrides((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    apiDeleteImage(sectionId);
  }, []);

  const resetChapter = useCallback((chapterId) => {
    const defaultChapter = defaultManualContent.chapters.find((c) => c.id === chapterId);
    if (!defaultChapter) return;
    setContent((prev) => {
      const next = deepClone(prev);
      const idx = next.chapters.findIndex((c) => c.id === chapterId);
      if (idx === -1) return prev;
      next.chapters[idx] = deepClone(defaultChapter);
      persist(next);
      return next;
    });
    setImageOverrides((prev) => {
      const next = { ...prev };
      defaultChapter.sections.forEach((s) => {
        delete next[s.id];
        apiDeleteImage(s.id);
      });
      return next;
    });
  }, [persist]);

  const resetAll = useCallback(() => {
    setContent(deepClone(defaultManualContent));
    setImageOverrides({});
    localStorage.removeItem(STORAGE_KEY);
    apiClearImages();
  }, []);

  const saveNow = useCallback(() => {
    persist(content);
  }, [content, persist]);

  return {
    content,
    imageOverrides,
    updateMeta,
    updateSection,
    updateStepItem,
    addStep,
    removeStep,
    moveStep,
    updateImage,
    removeImageOverride,
    resetChapter,
    resetAll,
    saveNow,
  };
};

export default useManualStore;
